// Composer Studio — 미리보기 팝아웃 창.
//
// 부모(EditorPage)가 window.open으로 여는 별도 브라우저 창. 에디터 크롬(팔레트/속성/탭) 없이
// 미리보기 렌더 + 뷰 모드 토글(데스크톱 / 바·폴드형)만 제공한다.
//
// 뷰 모드: '데스크톱'(단일 데스크톱 폭) / '바·폴드'(바형+폴드형을 나란히 함께 표시 — 모바일 비교용).
// 모드 선택 시 창 크기 자체를 해당 모드 크기로 resizeTo(실제 디바이스 창 시뮬레이션). 문서 레이아웃
// (A4)은 불변이고, 각 프레임 폭에 맞춰 스케일(폭 맞춤)만 한다.
//
// ⚠️ **보고서(양식) 전용 창**이다(2026-07-22 사용자 확정). 작성 페이지는 캔버스의 "비교" 보기가
// 상위 호환 — 실데이터(prefill)·반복 실행·등록형 위젯·작성 chrome까지 갖춘 실렌더를 뷰별로 나란히
// 띄운다(EditorPage compareView). 여기에 그걸 복제하면 진입점만 둘로 늘고 값이 빈 사본이 남는다.
// 부모가 viewMode='writeForm'을 보내면 안내 화면만 표시한다(진입은 부모에서 이미 차단).
//
// 데이터 흐름: mount 시 BroadcastChannel을 열고 `ready` 방송 → 부모가 현재 {template, content}로
// `sync` 응답 → 이후 부모의 content/template 변경마다 `sync`(작성폼 입력은 부모가 debounce). 렌더는
// 부모 미리보기와 동일하게 mergePreviewContent → applySimulatedApproval → PagedEditBody(순수
// 클라이언트, 서버 왕복 없음).

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  pagePx,
  resolveDetailChromeLayout,
  resolveDetailChromeOverflow,
  estimateDetailChromeButtonWidth,
  ChevronLeftIcon,
  type ResolvedDetailChromeLayoutItem,
  type DetailChromeSimContext,
  type DetailChromeTemplateCapabilities,
} from '@safetyhub/report-composer';
import type { ReportTemplate } from '@safetyhub/report-composer';
import { Button, buttonVariantClass } from '@safetyhub/ui';
import type {
  ComposerDevicePreset,
  DetailChromeActionId,
  DetailChromeConfig,
  DetailChromeStatus,
} from '@safetyhub/shared';
import {
  COMPOSER_VIEWS,
  DETAIL_CHROME_ACTION_LABELS,
  DETAIL_CHROME_STATUS_LABELS,
  DETAIL_CHROME_STATUSES,
  findPostSubmitFlow,
  isTemplateDelegationEnabled,
} from '@safetyhub/shared';
import {
  PREVIEW_POPOUT_CHANNEL,
  type PreviewPopoutMessage,
  type PreviewSyncPayload,
} from './previewPopoutChannel';
import { DetailChromePropertyEditor } from './DetailChromePropertyEditor';
import {
  PagedEditBody,
  mergePreviewContent,
  applySimulatedApproval,
  COMPOSER_SPECIAL_COMPONENTS,
} from './previewRender';
import { toBindingProvenance } from './previewLinkedReport';

/**
 * 프레임 카테고리 — 슬롯 고정(데스크톱 탭=desktop / 바·폴드 탭 좌=bar·우=fold). 각 카테고리의
 * 실기기 해상도 프리셋(COMPOSER_VIEWS.devices — HD/iPhone 15/갤럭시 Z 폴드 등) 중 골라 그 폭으로 실측.
 */
type FrameCat = 'desktop' | 'bar' | 'fold';
const CAT_DEVICES: Record<FrameCat, ComposerDevicePreset[]> = {
  desktop: COMPOSER_VIEWS.find((v) => v.key === 'desktop')?.devices ?? [],
  bar: COMPOSER_VIEWS.find((v) => v.key === 'bar')?.devices ?? [],
  fold: COMPOSER_VIEWS.find((v) => v.key === 'fold')?.devices ?? [],
};
/** 슬롯의 선택 device(범위 밖이면 첫 프리셋). */
function deviceOf(cat: FrameCat, idx: number): ComposerDevicePreset {
  const list = CAT_DEVICES[cat];
  return list[idx] ?? list[0] ?? { label: '—', width: 794, height: 1123 };
}

/** 뷰 모드 — 데스크톱 단독 / 바·폴드 나란히(각 영역 해상도는 프레임별 셀렉터로 변경). 선택 시 창 resizeTo. */
type ViewMode = 'desktop' | 'bar-fold';
const MODE_WINDOW: Record<ViewMode, { width: number; height: number }> = {
  desktop: { width: 1320, height: 960 },
  // 바+폴드 나란히 + gap/padding 여유(각 프레임 해상도 변경 시에도 넉넉하게).
  'bar-fold': { width: 1400, height: 960 },
};

const MODE_STORAGE_KEY = 'composer-preview-popout:mode';
// 프레임별 해상도(단일=데스크톱 탭 / 좌·우=바·폴드 탭 각 영역) — 개별 유지.
const FRAMES_STORAGE_KEY = 'composer-preview-popout:frames';

function loadMode(): ViewMode {
  try {
    const raw = localStorage.getItem(MODE_STORAGE_KEY);
    if (raw === 'desktop' || raw === 'bar-fold') return raw;
  } catch {
    // 접근 실패 = 기본값
  }
  return 'desktop';
}

/** 슬롯별 선택 device index — 데스크톱 탭 / 바·폴드 탭 좌(bar)·우(fold). 각각 유지. */
interface DeviceSel {
  desktop: number;
  bar: number;
  fold: number;
}
const DEFAULT_DEVICE_SEL: DeviceSel = { desktop: 0, bar: 0, fold: 0 };

function clampIdx(v: unknown, len: number): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < len ? v : 0;
}

function loadDevices(): DeviceSel {
  try {
    const raw = localStorage.getItem(FRAMES_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Record<FrameCat, unknown>>;
      return {
        desktop: clampIdx(p.desktop, CAT_DEVICES.desktop.length),
        bar: clampIdx(p.bar, CAT_DEVICES.bar.length),
        fold: clampIdx(p.fold, CAT_DEVICES.fold.length),
      };
    }
  } catch {
    // 파싱/접근 실패 = 기본값
  }
  return DEFAULT_DEVICE_SEL;
}

/**
 * 자식(A4 문서)을 scale로 축소해 지정 폭에 맞춘다. transform:scale은 레이아웃 크기에 영향이 없어
 * 스크롤이 깨지므로, 자식 실측(offset*scale)만큼 바깥 래퍼가 공간을 확보한다(세로 스크롤 정상화).
 * scale=1(폭 여유)이면 그대로.
 */
function ScaledDocument({ scale, children }: { scale: number; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div style={{ width: size.w * scale, height: size.h * scale }}>
      <div
        ref={ref}
        style={{ width: 'fit-content', transform: `scale(${scale})`, transformOrigin: 'top left' }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * 디바이스 프레임 — 라벨 + A4 문서를 폭 맞춤 스케일로 렌더. **Ctrl+휠로 이 프레임만 독립 확대/축소**
 * (바·폴드 나란히 비교 시 각각 zoom). 일반 휠은 통과(전체 스크롤). 프레임별 zoom state라 상호 독립.
 */
function DeviceFrame({
  frame,
  pageWidth,
  headerBar,
  controlBar,
  frameSelector,
  children,
}: {
  frame: { label: string; width: number };
  pageWidth: number;
  // 상세 chrome 편집 시 프레임 안에 flow로 얹는 헤더/컨트롤 바(문서는 그 사이 스크롤 영역). 미지정=없음.
  headerBar?: ReactNode;
  controlBar?: ReactNode;
  // 이 프레임의 해상도 변경 UI(프레임 라벨 대신 헤더에 표시). 미지정 시 라벨만.
  frameSelector?: ReactNode;
  children: ReactNode;
}) {
  const baseScale = Math.min(1, frame.width / pageWidth);
  const [zoom, setZoom] = useState(1);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    // Ctrl+휠만 가로채 확대/축소. 브라우저 기본 페이지 줌을 막기 위해 non-passive + preventDefault.
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(4, Math.max(0.2, z * (e.deltaY < 0 ? 1.1 : 1 / 1.1))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  const scale = baseScale * zoom;
  return (
    <div className="flex min-h-0 flex-col items-center">
      <div className="mb-1 flex items-center gap-1.5 text-xs tabular-nums text-gray-500">
        {frameSelector ?? <span>{frame.label}</span>}
        <span>· {frame.width}px · {Math.round(scale * 100)}%</span>
        {zoom !== 1 && (
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="text-brand-500 hover:underline"
            title="이 프레임 zoom 리셋"
          >
            리셋
          </button>
        )}
      </div>
      {/* 디바이스 화면 = 고정 폭·전체 높이 뷰포트. 상세 chrome 편집이면 헤더/컨트롤 바를 상·하 고정하고
          문서만 가운데 스크롤 영역에서 줌/스크롤(실제 상세 페이지 "본문만 스크롤" 재현). */}
      <div
        className="flex min-h-0 flex-1 flex-col bg-white shadow-sm"
        style={{ width: frame.width }}
      >
        {headerBar}
        {/* Ctrl+휠 zoom은 이 영역 안에서 콘텐츠를 확대/축소하고 넘치면 내부 스크롤(영역 크기 자체는 불변). */}
        <div ref={wrapRef} className="min-h-0 flex-1 overflow-auto">
          <ScaledDocument scale={scale}>{children}</ScaledDocument>
        </div>
        {controlBar}
      </div>
    </div>
  );
}

/** 드롭다운 버튼(예: 결재 ▼)의 메뉴 항목 미리보기 — 실제 web은 상황별로 활성/비활성. */
interface ChromeBarMenuItem {
  label: string;
  enabled: boolean;
  /** 비활성 사유(툴팁). */
  reason?: string;
}

/** chrome 편집 바 위 버튼 하나 — 라벨(override→기본) + mandatory(primary) + 선택 하이라이트.
 *  menu: cancelSubmit처럼 실제로는 드롭다운(결재 ▼)인 버튼의 항목들(결재/결재취소/재전송) — hover로 미리보기. */
function ChromeBarButton({
  item,
  selected,
  onSelect,
  menu,
}: {
  item: ResolvedDetailChromeLayoutItem;
  selected: boolean;
  onSelect: () => void;
  menu?: ChromeBarMenuItem[];
}) {
  const label = item.labelOverride ?? DETAIL_CHROME_ACTION_LABELS[item.actionId];
  // 드롭다운 아닌 단일 버튼이 enableWhen 미충족(enabled===false)이면 비활성 표시(회색). 단 클릭 선택은
  // 유지(native disabled 미사용) — 저작자가 비활성 버튼도 선택해 조건을 편집할 수 있어야 함.
  const showDisabled = !menu && item.enabled === false;
  // 삭제=danger(붉은계열). 강조=primaryWhen(scope) 평가 결과(primary) 우선, 없으면 mandatory 기본.
  const isDanger = item.actionId === 'delete';
  const isPrimary = item.primary ?? item.mandatory;
  const tone = showDisabled
    ? 'bg-gray-100 text-gray-400 border border-gray-200'
    : isDanger
      ? 'bg-white text-red-600 border border-red-300 hover:bg-red-50'
      : isPrimary
        ? // 실제 상세 액션바(rc ReportWriteShell)가 primary를 `buttonClassName('primary')`로
          // 그리므로, 액자 안 재현도 같은 출처에서 색만 빌린다(치수는 이 창이 따로 쥔다).
          buttonVariantClass('primary')
        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50';
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        title={showDisabled ? item.disabledReason : undefined}
        className={`rounded-control px-3 py-1.5 text-sm font-medium transition-colors ${tone} ${
          selected ? 'ring-2 ring-amber-400 ring-offset-1' : ''
        }`}
      >
        {label}
        {menu && <span className="ml-0.5 text-[10px] opacity-70">▾</span>}
      </button>
      {menu && (
        // 하단 컨트롤 바라 위로 열림(bottom-full). hover 시 표시(미리보기 — 실제 클릭은 버튼 선택).
        <div className="pointer-events-none absolute bottom-full left-0 z-10 mb-1 hidden min-w-[10rem] rounded-card border border-gray-200 bg-white p-1 shadow-lg group-hover:block">
          {menu.map((m) => (
            <div
              key={m.label}
              title={m.enabled ? undefined : m.reason}
              className={`flex items-center justify-between gap-2 rounded px-2 py-1 text-xs ${
                m.enabled ? 'text-gray-700' : 'text-gray-300'
              }`}
            >
              <span className={m.enabled ? '' : 'line-through'}>{m.label}</span>
              {!m.enabled && <span className="text-[9px] text-gray-400">비활성</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** 접힌 버튼 '더보기' 드롭다운 — 항목 클릭해 선택·편집(더보기 자체는 자동 생성이라 미선택).
 *  hover-out 시 250ms 지연 후 닫힘(트리거→메뉴 전환 중 gap에서 즉시 닫히지 않게). direction: 헤더=아래/하단바=위. */
function ChromeMoreMenu({
  menu,
  selected,
  onSelect,
  direction,
  alignRight,
}: {
  menu: ResolvedDetailChromeLayoutItem[];
  selected: DetailChromeActionId | null;
  onSelect: (actionId: DetailChromeActionId) => void;
  direction: 'up' | 'down';
  alignRight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);
  const openNow = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(true);
  }, []);
  const closeSoon = useCallback(() => {
    timerRef.current = window.setTimeout(() => setOpen(false), 250);
  }, []);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  return (
    <div className="relative shrink-0" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        type="button"
        className="rounded-control border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50"
      >
        더보기 ⋯
      </button>
      {open && (
        <div
          className={`absolute ${direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'} ${
            alignRight ? 'right-0' : 'left-0'
          } z-10 min-w-[9rem] rounded-card border border-gray-200 bg-white p-1 shadow-lg`}
        >
          {menu.map((m) => (
            <button
              key={m.actionId}
              type="button"
              onClick={() => onSelect(m.actionId)}
              className={`block w-full rounded px-2 py-1 text-left text-xs hover:bg-gray-100 ${
                selected === m.actionId ? 'bg-amber-50 text-amber-700' : 'text-gray-700'
              }`}
            >
              {m.labelOverride ?? DETAIL_CHROME_ACTION_LABELS[m.actionId]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 프레임 폭에 맞춘 상세 chrome 헤더/컨트롤 바. 세 슬롯 모두 fit 기반 오버플로우 — 넘치면 '더보기'로 접힘
 * (헤더도 높이를 키우지 않고 접는다). 버튼 클릭 → 선택(속성 패널 대상).
 */
function chromeBars(
  config: DetailChromeConfig | undefined,
  status: DetailChromeStatus,
  frameWidth: number,
  selected: DetailChromeActionId | null,
  onSelect: (actionId: DetailChromeActionId) => void,
  sim: DetailChromeSimContext,
  scope: Record<string, unknown>,
  capabilities: DetailChromeTemplateCapabilities,
): { header: ReactNode; control: ReactNode } {
  // sim(결재라인 유무·진행지점)으로 web 게이팅 근사 + scope(조건 변수)로 범용 enableWhen/visibleWhen 평가
  // + capabilities(양식 능력)로 도달 불가 액션 제외(위임 미지원 양식의 위임 동사).
  const layout = resolveDetailChromeLayout(config, status, { sim, scope, capabilities });
  // 결재 진행 버튼(submit=초안 / cancelSubmit=결재중)은 실제로는 '결재 ▼' 드롭다운 — 안에 결재/결재 취소/
  // 결재 재전송이 상황별 활성/비활성으로 들어간다(web buildApprovalActions). 취소를 바깥으로 빼지 않고
  // 메뉴 항목으로. 초안: 결재(enableWhen 반영)/취소·재전송 비활성 / 결재중: 결재 비활성/취소(진행지점별)·재전송.
  const menuOf = (it: ResolvedDetailChromeLayoutItem): ChromeBarMenuItem[] | undefined => {
    if (it.actionId === 'submit') {
      // 초안 결재 드롭다운 — '결재' 활성은 항목 enableWhen(사전조건) 평가 결과(enabled).
      return [
        { label: '결재', enabled: it.enabled ?? true, reason: it.disabledReason },
        { label: '결재 취소', enabled: false, reason: '결재 진행 중이 아닙니다' },
        { label: '결재 재전송', enabled: false, reason: '재전송 대상 없음' },
      ];
    }
    if (it.actionId === 'cancelSubmit') {
      const partial = sim.pendingPhase === 'partial';
      return [
        { label: '결재', enabled: false, reason: '이미 결재 요청됨' },
        {
          label: '결재 취소',
          enabled: !partial,
          reason: partial ? '이미 결재가 진행되어 취소 불가' : undefined,
        },
        { label: '결재 재전송', enabled: true },
      ];
    }
    return undefined;
  };
  // 재제출(resubmit)은 코드 소유 활성 조건 = editedAfterRejection(반려 후 본문 수정). 저작 enableWhen이
  // 없으면(기본) 이 built-in 값으로 미리보기 활성/비활성 표시(결재 취소의 진행지점 게이팅과 동형).
  const withBuiltinEnable = (it: ResolvedDetailChromeLayoutItem): ResolvedDetailChromeLayoutItem => {
    if (it.actionId === 'resubmit' && it.enabled === undefined) {
      return {
        ...it,
        enabled: !!scope.editedAfterRejection,
        disabledReason: '본문 수정 후 재제출 가능합니다',
      };
    }
    return it;
  };
  const btn = (raw: ResolvedDetailChromeLayoutItem) => {
    const it = withBuiltinEnable(raw);
    return (
      <ChromeBarButton
        key={it.actionId}
        item={it}
        selected={selected === it.actionId}
        onSelect={() => onSelect(it.actionId)}
        menu={menuOf(it)}
      />
    );
  };
  // 슬롯 하나를 fit 기반 오버플로우로 렌더 — 넘치면 '더보기'로 접음(헤더도 높이 안 키우고 접힘).
  const renderSlot = (
    slotItems: ResolvedDetailChromeLayoutItem[],
    budget: number,
    direction: 'up' | 'down',
    alignRight?: boolean,
  ) => {
    const { visible: vis, menu: mnu } = resolveDetailChromeOverflow(slotItems, budget);
    return (
      <>
        {vis.map(btn)}
        {mnu.length > 0 && (
          <ChromeMoreMenu
            menu={mnu}
            selected={selected}
            onSelect={onSelect}
            direction={direction}
            alignRight={alignRight}
          />
        )}
      </>
    );
  };
  // 헤더 좌/우는 폭을 나눠 쓰므로, 각 측 예산 = 전체 폭 - 반대측 자연폭 - '목록' 고정폭. 양측 자연폭 합이
  // 폭 안이면 서로 안 접힘(halfBudget 고정 분할은 한쪽이 작아도 반대쪽을 조기 접히게 해 오작동).
  const estW = (it: ResolvedDetailChromeLayoutItem) =>
    estimateDetailChromeButtonWidth(it.labelOverride ?? DETAIL_CHROME_ACTION_LABELS[it.actionId]);
  const MOKROK_W = 64; // 'chevron + 목록' 고정 폭(대략치 — fit 순서 판정용)
  const leftItemsNat = layout.headerLeft.reduce((s, it) => s + estW(it), 0);
  const rightItemsNat = layout.headerRight.reduce((s, it) => s + estW(it), 0);
  const header = (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center gap-1.5">
        {/* 진입점 back-link — detailChrome 저작 대상이 아니라 셸(ReportWriteShell/StickyHeader)이 상태
            무관하게 항상 그리는 고정 요소. 실제 라벨은 진입점(목록/점검)에 따라 달라지므로 미리보기는
            대표값 '목록'을 비편집(회색) 정적 표시 — 실제 헤더와 동형으로 보이게. */}
        <span
          className="flex shrink-0 cursor-default items-center gap-1 text-sm font-medium text-gray-400"
          title="진입점에 따라 자동 표시되는 뒤로가기(목록/점검 등). 저작 대상 아님."
        >
          <ChevronLeftIcon className="w-4 h-4" />목록
        </span>
        {renderSlot(layout.headerLeft, frameWidth - MOKROK_W - rightItemsNat, 'down', false)}
      </div>
      <div className="flex items-center justify-end gap-1.5">
        {renderSlot(layout.headerRight, frameWidth - MOKROK_W - leftItemsNat, 'down', true)}
      </div>
    </div>
  );
  const control = (
    <div className="flex shrink-0 items-center justify-end gap-1.5 border-t border-gray-200 bg-white px-3 py-2.5">
      {layout.actionBar.length === 0 ? (
        <span className="text-xs text-gray-300">이 상태에 하단 버튼 없음</span>
      ) : (
        renderSlot(layout.actionBar, frameWidth, 'up', true)
      )}
    </div>
  );
  return { header, control };
}

export default function PreviewPopoutPage() {
  const [payload, setPayload] = useState<PreviewSyncPayload | null>(null);
  const [mode, setMode] = useState<ViewMode>(() => loadMode());
  // 슬롯별 선택 device index(데스크톱 탭 / 바·폴드 탭 좌·우) — 각각 변경·유지.
  const [devSel, setDevSel] = useState<DeviceSel>(() => loadDevices());
  const setDevice = useCallback((cat: FrameCat, idx: number) => {
    setDevSel((s) => {
      const next = { ...s, [cat]: idx };
      try {
        localStorage.setItem(FRAMES_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // 저장 실패 무시
      }
      return next;
    });
  }, []);

  // ── 상세 chrome(헤더/컨트롤 바) 편집 모드 ──
  // 부모의 enterChromeEdit 방송 시 켜진다. chromeConfig는 편집 세션 로컬 소스(부모 재sync가 라벨 입력을
  // mid-typing 리셋하지 않도록 로컬이 권위) — onChange마다 부모로 detailChromeEdit 방송 + 로컬 갱신.
  const [chromeEdit, setChromeEdit] = useState(false);
  const [chromeStatus, setChromeStatus] = useState<DetailChromeStatus>('DRAFT');
  const [chromeSelected, setChromeSelected] = useState<DetailChromeActionId | null>(null);
  const [chromeConfig, setChromeConfig] = useState<DetailChromeConfig | undefined>(undefined);
  // 시뮬레이션 축(미리보기 전용, 저장 안 함) — 결재라인 사용 여부(초기값=템플릿 approvalLineMeta) +
  // 결재중(PENDING) 진행지점. 실제 web 게이팅(결재/완료 택일·취소 가능 시점)을 미리보기에서 재현.
  const [simHasApprovalLine, setSimHasApprovalLine] = useState(true);
  const [pendingPhase, setPendingPhase] = useState<'before' | 'partial'>('before');
  // 범용 조건 변수 시뮬레이션 값(chrome-scope) — enableWhen/visibleWhen 평가용. 완료 여부 boolean.
  const [simContactSigned, setSimContactSigned] = useState(false);
  const [simAuthorSigned, setSimAuthorSigned] = useState(false);
  const [simEditedAfterRejection, setSimEditedAfterRejection] = useState(false);
  // 다회차 교육(결정 2026-09-08) — 실화면에서는 공유 술어 `isMultiSessionEducation`이 문서의
  // 교육 기간에서 파생하지만, 미리보기에는 문서가 없으므로 다른 조건 변수와 같이 토글로 둔다.
  // 초기값 false = 기간 미지정(단일회차) — dev 실측에서 가장 흔한 상태다.
  const [simMultiSession, setSimMultiSession] = useState(false);
  // 뷰어 역할 — 상세를 누가 보느냐(작성자 / 결재자). 결재중에 결재▼(작성자)·결재하기(결재자) 분리 확인.
  const [viewerRole, setViewerRole] = useState<'author' | 'approver'>('author');
  // 채널 ref — 편집 onChange가 mount-once 채널을 넘어 부모에 방송하기 위함.
  const channelRef = useRef<BroadcastChannel | null>(null);

  // 현재 하이라이트 대상 + 원복용 이전 인라인 값 보관. 부모의 미배치 풀 hover(highlight-node)로 구동.
  const highlightRef = useRef<{ el: HTMLElement; outline: string; outlineOffset: string } | null>(null);

  // BroadcastChannel 연결 — sync 수신 시 payload 갱신. mount 시 ready 방송(부모가 초기 스냅샷 응답).
  // 부모 리스너가 아직 미부착일 수 있으므로 첫 sync 도착 전까지 짧게 ready를 재방송한다(핸드셰이크 경합 방지).
  useEffect(() => {
    const ch = new BroadcastChannel(PREVIEW_POPOUT_CHANNEL);
    channelRef.current = ch;
    let gotSync = false;
    // 이전 하이라이트 원복(대상이 재렌더로 분리됐어도 인라인 style 세팅은 no-op — null-safe).
    const clearHighlight = () => {
      const prev = highlightRef.current;
      if (!prev) return;
      prev.el.style.outline = prev.outline;
      prev.el.style.outlineOffset = prev.outlineOffset;
      highlightRef.current = null;
    };
    ch.onmessage = (ev: MessageEvent<PreviewPopoutMessage>) => {
      const msg = ev.data;
      if (msg.type === 'sync') {
        gotSync = true;
        setPayload({
          template: msg.template,
          content: msg.content,
          hasRealData: msg.hasRealData,
          viewMode: msg.viewMode,
        });
      } else if (msg.type === 'highlight-node') {
        clearHighlight();
        if (!msg.nodeId) return;
        // body 노드 앵커(RenderTemplate가 data-composer-node-id 부여). 페이지 분할 렌더면 첫 매치.
        const el = document.querySelector<HTMLElement>(`[data-composer-node-id="${msg.nodeId}"]`);
        if (!el) return;
        // 레이아웃 중립 하이라이트 — outline만(border/padding 금지, WYSIWYG 렌더 훼손 방지).
        highlightRef.current = {
          el,
          outline: el.style.outline,
          outlineOffset: el.style.outlineOffset,
        };
        el.style.outline = '2px solid #6366f1'; // brand-500
        el.style.outlineOffset = '2px';
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else if (msg.type === 'enterChromeEdit') {
        // 부모 "F8에서 편집" 진입 — chrome 편집 모드 켜기(seed는 아래 effect가 payload 도착 시).
        setChromeEdit(true);
      } else if (msg.type === 'closed') {
        // 부모가 닫힘을 알림 — 렌더는 마지막 스냅샷 유지(창을 강제로 닫지 않음).
      }
    };
    const postReady = () => ch.postMessage({ type: 'ready' } satisfies PreviewPopoutMessage);
    postReady();
    const retries = [150, 400, 900].map((ms) =>
      window.setTimeout(() => {
        if (!gotSync) postReady();
      }, ms),
    );
    return () => {
      retries.forEach((t) => window.clearTimeout(t));
      clearHighlight();
      ch.close();
      channelRef.current = null;
    };
  }, []);

  // chrome 편집 진입 시 현재 template.detailChrome로 로컬 편집본 seed(한 번). payload가 아직이면
  // (방금 window.open) payload 도착 시 재실행돼 seed. 이후 편집은 로컬 chromeConfig가 권위(리셋 방지).
  const chromeSeededRef = useRef(false);
  useEffect(() => {
    if (!chromeEdit) {
      chromeSeededRef.current = false;
      return;
    }
    if (chromeSeededRef.current || !payload) return;
    chromeSeededRef.current = true;
    setChromeConfig(payload.template.detailChrome);
    // 결재라인 사용 초기값 = 템플릿 approvalLineMeta.defaultApprovalLine 유무(완료형이면 false).
    setSimHasApprovalLine(
      (payload.template.approvalLineMeta?.defaultApprovalLine?.length ?? 0) > 0,
    );
  }, [chromeEdit, payload]);

  // 편집 결과 → 로컬 갱신(즉시 반영) + 부모로 방송(template.detailChrome 갱신 → 저장 대상).
  const handleChromeChange = useCallback((next: DetailChromeConfig | undefined) => {
    setChromeConfig(next);
    channelRef.current?.postMessage(
      { type: 'detailChromeEdit', detailChrome: next } satisfies PreviewPopoutMessage,
    );
  }, []);

  // 저장 요청 — 부모(EditorPage)가 실제 저장 실행(팝업엔 저장 로직 없음). 저장 버튼/Ctrl+S 공용.
  const requestSave = useCallback(() => {
    channelRef.current?.postMessage({ type: 'save' } satisfies PreviewPopoutMessage);
  }, []);

  // 팝업 창에서 Ctrl/Cmd+S → 브라우저 "페이지 저장" 다이얼로그 차단 + 부모로 저장 요청 방송.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        requestSave();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [requestSave]);

  useEffect(() => {
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {
      // 저장 실패는 무시(런타임 무영향)
    }
  }, [mode]);

  // 모드 선택(+초기 복원) 시 창 크기 자체를 해당 모드 크기로 리사이즈해 실제 디바이스 창을 시뮬레이션.
  // window.open으로 열린 팝업이라 resizeTo 허용. 외곽(크롬 포함) 기준이라 근사 — 차단 환경은 무시.
  useEffect(() => {
    const w = MODE_WINDOW[mode];
    try {
      window.resizeTo(w.width, w.height);
    } catch {
      // 리사이즈 차단(브라우저 정책) 무시
    }
  }, [mode]);

  const template: ReportTemplate | null = payload?.template ?? null;
  const pageWidth = template ? pagePx(template.page).w : 794;

  // 미리보기와 동일한 병합/시뮬레이션 결재 주입 — payload 변경 시에만 재계산(입력 keystroke 반영).
  const data = useMemo(() => {
    if (!payload) return null;
    const merged = mergePreviewContent(payload.template, payload.content);
    return applySimulatedApproval(payload.template, merged, payload.approvalSignedLevel);
  }, [payload]);

  // 값 출처 표시 재료 — 채널은 구조적 복제라 배열로 건너오므로 여기서 Set 형상으로 되돌린다.
  const bindingProvenance = useMemo(
    () => (payload?.provenance ? toBindingProvenance(payload.provenance) : undefined),
    [payload?.provenance],
  );

  // 문서 엘리먼트 — 프레임마다 새로 생성(같은 엘리먼트 재사용 대신 함수로 독립 인스턴스).
  const renderDoc = () =>
    template && data ? (
      <PagedEditBody
        template={template}
        data={data}
        specialComponents={COMPOSER_SPECIAL_COMPONENTS}
        // 미리보기 탭과 동일 정책 — 실데이터면 미해소(live/보고서-스코프)만 amber, 아니면 전체 회색.
        bindingPlaceholders={payload?.hasRealData ? 'unresolvable-only' : true}
        // 부모 미리보기와 같은 값 출처 밑줄(모사/연결 보고서) — 재료는 sync payload 경유.
        bindingProvenance={bindingProvenance}
      />
    ) : null;

  // 미리보기 시뮬레이션 컨텍스트 — 결재라인 유무 + 결재중 진행지점 + 뷰어 역할(결재중에만 의미, all=최대집합).
  const chromeSim: DetailChromeSimContext = {
    hasApprovalLine: simHasApprovalLine,
    pendingPhase,
    viewerRole: chromeStatus === 'PENDING_APPROVAL' ? viewerRole : undefined,
  };
  // 양식 능력 — 편집 중인 template에서 읽는다(web `templateSnapshot`과 같은 낱말).
  // 그 능력이 없으면 해당 버튼은 런타임에 절대 뜨지 않으므로 저작 표면에서도 그리지 않는다.
  //  - 위임 작성: `delegationMeta.enabled`
  //  - 제출 후 흐름: `policy.postSubmit.flow`가 **등록된** 키일 때만(`findPostSubmitFlow`) —
  //    미등록 키는 web 소비처도 "흐름 없음"으로 읽으므로 여기서도 같게 읽어야 갈리지 않는다.
  // (참조 동일성 유지 — 속성 패널의 useMemo/useCallback 의존성이라 매 렌더 새 객체면 헛돈다.)
  const chromeCapabilities: DetailChromeTemplateCapabilities = useMemo(
    () => ({
      delegationEnabled: isTemplateDelegationEnabled(template),
      postSubmitFlowDeclared: !!findPostSubmitFlow(template?.policy?.postSubmit?.flow),
    }),
    [template],
  );
  // 범용 조건(enableWhen/visibleWhen) 평가용 chrome-scope 변수값 — DETAIL_CHROME_CONDITION_VARS 어휘.
  const chromeScope: Record<string, unknown> = {
    status: chromeStatus,
    hasApprovalLine: simHasApprovalLine,
    contactSignatureComplete: simContactSigned,
    authorSigned: simAuthorSigned,
    editedAfterRejection: simEditedAfterRejection,
    isMultiSession: simMultiSession,
  };
  // 프레임 폭별 상세 chrome 바(편집 모드일 때만) — actionBar는 그 프레임 해상도 기준 오버플로우.
  const barsFor = (frameWidth: number) =>
    chromeEdit
      ? chromeBars(
          chromeConfig,
          chromeStatus,
          frameWidth,
          chromeSelected,
          setChromeSelected,
          chromeSim,
          chromeScope,
          chromeCapabilities,
        )
      : { header: undefined, control: undefined };
  // 슬롯별 선택 device(프레임) + 그 폭 기준 chrome 바.
  const desktopDev = deviceOf('desktop', devSel.desktop);
  const barDev = deviceOf('bar', devSel.bar); // 바·폴드 탭 좌
  const foldDev = deviceOf('fold', devSel.fold); // 바·폴드 탭 우
  const desktopBars = barsFor(desktopDev.width);
  const leftBars = barsFor(barDev.width);
  const rightBars = barsFor(foldDev.width);

  // 프레임 해상도 셀렉터 — 그 카테고리의 실기기 프리셋(COMPOSER_VIEWS.devices) 드롭다운. DeviceFrame 헤더에 표시.
  const renderResSelector = (cat: FrameCat, current: number) => (
    <select
      value={current}
      onChange={(e) => setDevice(cat, Number(e.target.value))}
      className="rounded border border-gray-300 bg-white px-1 py-0.5 text-[11px] font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand-400"
      aria-label="해상도 선택"
    >
      {CAT_DEVICES[cat].map((d, i) => (
        <option key={d.label} value={i}>
          {d.label}
        </option>
      ))}
    </select>
  );

  // 뷰 모드 토글 — 보고서/작성 페이지 미리보기 공용(모드·창 리사이즈 동작 동일).
  const modeToggle = (
    <div className="inline-flex rounded-control border border-gray-300 bg-white p-0.5">
      {(
        [
          { id: 'desktop', label: '데스크톱' },
          { id: 'bar-fold', label: '바 · 폴드형' },
        ] as { id: ViewMode; label: string }[]
      ).map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => setMode(m.id)}
          aria-pressed={mode === m.id}
          className={`px-3 py-1 text-xs rounded transition-colors ${
            mode === m.id ? 'bg-gray-700 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );

  // 작성 페이지 모드 — 이 팝아웃은 **보고서(양식) 전용**이다(2026-07-22 사용자 확정).
  // 작성 페이지는 캔버스의 "비교" 보기가 상위 호환: 실데이터(prefill)·반복 실행·등록형 위젯·작성
  // chrome까지 갖춘 실렌더를 뷰별로 나란히 띄운다(EditorPage의 compareView). 팝아웃이 그걸 복제하면
  // 진입점만 둘로 늘고 값이 빈 부실한 사본이 남는다.
  // 부모에서 진입은 이미 막혀 있지만(작성 탭에서 버튼/F8 비활성), 팝아웃을 열어둔 채 작성 탭으로
  // 전환하면 여기로 오므로 깨진 화면 대신 안내를 보여준다.
  if (payload?.viewMode === 'writeForm') {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-gray-100 px-8 text-center">
        <p className="text-sm font-semibold text-gray-700">작성 페이지는 이 창에서 미리보지 않습니다</p>
        <p className="max-w-md text-xs leading-relaxed text-gray-500">
          에디터 캔버스의 <strong>비교</strong> 보기를 사용하세요 — 실데이터·반복 영역·등록형 위젯까지
          실제와 같이 렌더한 화면을 뷰(데스크톱/폴드형/바형)별로 나란히 볼 수 있습니다.
        </p>
        <p className="text-xs text-gray-400">
          보고서(양식) 탭으로 전환하면 이 창이 다시 미리보기를 표시합니다.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* 상단 바 — 뷰 모드 토글(데스크톱 / 바·폴드) + 상세 chrome 편집 토글/상태 셀렉터 */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-white px-4 py-2">
        <span className="text-sm font-semibold text-gray-700">미리보기</span>
        {modeToggle}

        {template && (
          <>
            <span className="h-4 w-px bg-gray-300" aria-hidden />
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={chromeEdit}
                onChange={(e) => setChromeEdit(e.target.checked)}
              />
              <span className="font-medium">헤더·하단 컨트롤 바 편집</span>
            </label>
            {chromeEdit && (
              <>
                {/* 결재라인 사용(시뮬레이션 전용, 저장 안 함) — 초기값=템플릿 결재라인 설정. 끄면
                    완료형: DRAFT는 '완료' 버튼, 결재중·반려 상태 없음. 실제 web 게이팅을 미리보기에서 재현. */}
                <label
                  className="flex items-center gap-1.5 text-xs text-gray-600"
                  title="템플릿 결재라인 설정 시뮬레이션(저장 안 함). 끄면 완료형 — DRAFT는 '완료' 버튼, 결재중·반려 상태가 없습니다."
                >
                  <input
                    type="checkbox"
                    checked={simHasApprovalLine}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setSimHasApprovalLine(next);
                      // 완료형(next=false)엔 결재중·반려가 없으므로 그 상태 선택 중이면 초안으로 되돌림.
                      if (!next && (chromeStatus === 'PENDING_APPROVAL' || chromeStatus === 'REJECTED')) {
                        setChromeStatus('DRAFT');
                        setChromeSelected(null);
                      }
                    }}
                  />
                  <span className="font-medium">결재라인 사용</span>
                </label>
                <span className="h-4 w-px bg-gray-300" aria-hidden />
                {/* 루트 상태 — 완료형이면 결재중·반려 비활성. 결재중 선택 시 진행지점(before)로 리셋. */}
                <div className="flex items-center gap-1" role="tablist" aria-label="결재 진행 상태">
                  {DETAIL_CHROME_STATUSES.map((s) => {
                    const disabled =
                      !simHasApprovalLine && (s === 'PENDING_APPROVAL' || s === 'REJECTED');
                    return (
                      <button
                        key={s}
                        type="button"
                        role="tab"
                        aria-selected={s === chromeStatus}
                        disabled={disabled}
                        title={disabled ? '완료형(결재라인 미사용)에는 없는 상태' : undefined}
                        onClick={() => {
                          setChromeStatus(s);
                          setChromeSelected(null);
                          if (s === 'PENDING_APPROVAL') setPendingPhase('before');
                        }}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${
                          s === chromeStatus
                            ? 'bg-brand-600 text-white'
                            : disabled
                              ? 'cursor-not-allowed bg-gray-50 text-gray-300'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {DETAIL_CHROME_STATUS_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
                {/* 서브 상태 — 결재중(PENDING)일 때만 진행지점(결재 전 / 일부 결재)을 추가 선택. */}
                {chromeStatus === 'PENDING_APPROVAL' && (
                  <div className="flex items-center gap-1" role="tablist" aria-label="결재중 진행 지점">
                    {(
                      [
                        { id: 'before', label: '결재 전' },
                        { id: 'partial', label: '일부 결재' },
                      ] as { id: 'before' | 'partial'; label: string }[]
                    ).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        role="tab"
                        aria-selected={p.id === pendingPhase}
                        onClick={() => {
                          setPendingPhase(p.id);
                          setChromeSelected(null);
                        }}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${
                          p.id === pendingPhase
                            ? 'bg-brand-500 text-white'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
                {/* 뷰어 역할 — 결재중에만 의미(작성자=결재▼·삭제 / 결재자=결재하기). 다른 상태엔 결재자 액션이
                    없어 미표시. 실제 상세는 한 사람이 자기 역할 버튼만 봄(작성자==결재자면 둘 다). */}
                {chromeStatus === 'PENDING_APPROVAL' && (
                  <>
                    <span className="h-4 w-px bg-gray-300" aria-hidden />
                    <div className="flex items-center gap-1" role="tablist" aria-label="뷰어 역할">
                      <span className="text-[11px] text-gray-400">뷰어:</span>
                      {(
                        [
                          { id: 'author', label: '작성자' },
                          { id: 'approver', label: '결재자' },
                        ] as { id: 'author' | 'approver'; label: string }[]
                      ).map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          role="tab"
                          aria-selected={v.id === viewerRole}
                          onClick={() => {
                            setViewerRole(v.id);
                            setChromeSelected(null);
                          }}
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            v.id === viewerRole
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {/* 범용 조건 변수 시뮬레이션 — enableWhen/visibleWhen이 참조하는 chrome-scope 값 토글.
                    예: 결재 항목에 enableWhen='contactSignatureComplete'를 걸면 아래 토글로 활성/비활성 확인. */}
                <span className="h-4 w-px bg-gray-300" aria-hidden />
                <span className="text-[11px] text-gray-400">조건 변수:</span>
                <label
                  className="flex items-center gap-1 text-xs text-gray-600"
                  title="chrome-scope 변수 contactSignatureComplete 시뮬레이션 — enableWhen 조건 평가에 반영."
                >
                  <input
                    type="checkbox"
                    checked={simContactSigned}
                    onChange={(e) => setSimContactSigned(e.target.checked)}
                  />
                  <span>현장 담당자 서명 완료</span>
                </label>
                <label
                  className="flex items-center gap-1 text-xs text-gray-600"
                  title="chrome-scope 변수 authorSigned 시뮬레이션 — enableWhen/visibleWhen 조건 평가에 반영."
                >
                  <input
                    type="checkbox"
                    checked={simAuthorSigned}
                    onChange={(e) => setSimAuthorSigned(e.target.checked)}
                  />
                  <span>작성자 서명 완료</span>
                </label>
                <label
                  className="flex items-center gap-1 text-xs text-gray-600"
                  title="chrome-scope 변수 editedAfterRejection 시뮬레이션 — 반려 상태 재제출 활성 조건(코드 소유) + enableWhen 평가에 반영."
                >
                  <input
                    type="checkbox"
                    checked={simEditedAfterRejection}
                    onChange={(e) => setSimEditedAfterRejection(e.target.checked)}
                  />
                  <span>반려 후 본문 수정됨</span>
                </label>
                <label
                  className="flex items-center gap-1 text-xs text-gray-600"
                  title="chrome-scope 변수 isMultiSession 시뮬레이션 — 교육 기간이 여러 날인가(다회차). enableWhen/visibleWhen 조건 평가에 반영."
                >
                  <input
                    type="checkbox"
                    checked={simMultiSession}
                    onChange={(e) => setSimMultiSession(e.target.checked)}
                  />
                  <span>다회차 교육</span>
                </label>
                {/* 저장 — 편집 시 노출. 팝업엔 저장 로직이 없어 부모(EditorPage)로 저장 요청 방송(Ctrl+S 동일). */}
                <span className="h-4 w-px bg-gray-300" aria-hidden />
                <Button variant="primary" size="xs" onClick={requestSave} title="저장 (Ctrl+S)">
                  저장
                </Button>
              </>
            )}
          </>
        )}
      </div>

      {/* 문서 영역 + (편집 시) 좌측 저작 패널. aside를 DOM 먼저 둬 좌측 고정 — 창이 좁아도 패널 전체(w-96)가
          유지되고, 대신 문서 영역이 밀려 가로 스크롤(min-w-0 + overflow-auto)된다(패널 클립 방지). */}
      <div className="flex min-h-0 flex-1">
        {chromeEdit && (
          <aside className="w-96 shrink-0 overflow-auto border-r border-gray-300 bg-gray-100 p-3">
            <DetailChromePropertyEditor
              value={chromeConfig}
              onChange={handleChromeChange}
              status={chromeStatus}
              selected={chromeSelected}
              onSelect={setChromeSelected}
              capabilities={chromeCapabilities}
            />
          </aside>
        )}
        {/* 문서 영역 — 프레임(디바이스 화면)은 고정 크기로 내부 스크롤. min-w-0로 flex-1 축소 허용 + 가로 넘침 스크롤. */}
        <div className="min-w-0 flex-1 overflow-auto bg-gray-200 p-6">
        {template && data ? (
          mode === 'bar-fold' ? (
            // 바·폴드 나란히(비교) — w-max mx-auto: 여유 있으면 중앙, 좁으면 좌측부터(넘침은 가로 스크롤로
            // 접근 — justify-center면 좌측 프레임이 넘쳐 스크롤로도 못 봄).
            <div className="flex h-full w-max mx-auto gap-8">
              <DeviceFrame
                frame={barDev}
                pageWidth={pageWidth}
                headerBar={leftBars.header}
                controlBar={leftBars.control}
                frameSelector={renderResSelector('bar', devSel.bar)}
              >
                {renderDoc()}
              </DeviceFrame>
              <DeviceFrame
                frame={foldDev}
                pageWidth={pageWidth}
                headerBar={rightBars.header}
                controlBar={rightBars.control}
                frameSelector={renderResSelector('fold', devSel.fold)}
              >
                {renderDoc()}
              </DeviceFrame>
            </div>
          ) : (
            // 데스크톱 탭 — 단일 프레임. w-max mx-auto(여유 중앙 / 좁으면 좌측부터 스크롤).
            <div className="flex h-full w-max mx-auto">
              <DeviceFrame
                frame={desktopDev}
                pageWidth={pageWidth}
                headerBar={desktopBars.header}
                controlBar={desktopBars.control}
                frameSelector={renderResSelector('desktop', devSel.desktop)}
              >
                {renderDoc()}
              </DeviceFrame>
            </div>
          )
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            편집 창의 데이터를 기다리는 중…
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
